import * as React from 'react';
import Alert from '@cloudscape-design/components/alert';
import Box, { type BoxProps } from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { formatDelta } from './formatters';

export type KpiCardProps = {
  label: string;
  value: string;
  helperText?: string | null;
  deltaPct?: number | null;
  anomalyDetail?: string | null;
  /** Aceito por compat com os dashboards ainda não migrados; ignorado
   *  (o Container do Cloudscape não usa className). Em 2026-10-04 nenhum
   *  consumidor passa `className` — sai na 39-L (remoção do Tailwind/ui-shared
   *  do admin, ainda a fazer em `docs/sprint-39-admin-cloudscape.md`). */
  className?: string;
};

/** Cor do delta: verde sobe, vermelho cai, cinza estável/sem dado. */
const DIRECTION_COLOR: Record<'UP' | 'DOWN' | 'FLAT' | 'NA', NonNullable<BoxProps['color']>> = {
  UP: 'text-status-success',
  DOWN: 'text-status-error',
  FLAT: 'text-status-inactive',
  NA: 'text-status-inactive',
};

/**
 * Card de KPI dos dashboards BI do admin (Cloudscape `Container`): rótulo,
 * valor já formatado, delta vs período anterior e alerta de anomalia.
 *
 * Origem: sem issue — commit 498e432c6, 2026-05-04 15:52 +0200 (rota
 * `/dashboard/finance`); Cloudscape em 4686dbd44, 2026-06-08 (39-G).
 *
 * Contrato: `value` chega formatado (moeda, inteiro ou %, decidido pelo caller
 * — o card não sabe a unidade). `deltaPct` é fração (0,12 = +12 %), traduzida
 * por `formatDelta`; `null`/`undefined` = "sem dado". `anomalyDetail` não vazio
 * mostra um `Alert warning`.
 *
 * Usado por: `dashboard-finance/KpiCards.tsx`, `dashboard-usage/AllTimePrintedCard.tsx`,
 * `dashboard-usage-basic/TenantsCountCard.tsx` e `ProductsCountCard.tsx` (admin web).
 */
export function KpiCard({
  label,
  value,
  helperText,
  deltaPct,
  anomalyDetail,
}: KpiCardProps): React.JSX.Element {
  const delta = formatDelta(deltaPct ?? null);
  return (
    <Container>
      <SpaceBetween size="xxs">
        <Box variant="awsui-key-label">{label}</Box>
        <Box variant="h1">{value}</Box>
        <SpaceBetween direction="horizontal" size="xs">
          <Box variant="span" color={DIRECTION_COLOR[delta.direction]} fontWeight="bold">
            {delta.label}
          </Box>
          {helperText && (
            <Box variant="span" color="text-status-inactive">
              {helperText}
            </Box>
          )}
        </SpaceBetween>
        {anomalyDetail && <Alert type="warning">{anomalyDetail}</Alert>}
      </SpaceBetween>
    </Container>
  );
}
